import React, { useState } from "react";
import AdminModal from "components/admin/AdminModal";
import OgUserGroupFormModal from "./ogUserGroupFormModal";
import { formatFreeTokenAmount } from "./constants";
import { OgUserGroup } from "./type";
import {
  deleteOgUserGroup,
  getApiErrorMessage,
} from "apis/adminServices/userManagmentService";
import { useToastStore } from "store/toastStore";

interface ManageUserRolesModalProps {
  isOpen: boolean;
  onClose: () => void;
  groups: OgUserGroup[];
  loading: boolean;
  /** Refetches the groups after an add / edit so counts and cards stay in step. */
  onRefresh: () => void;
}

/**
 * Read-only list of the OG user groups. Adding a role and editing one both go
 * through `OgUserGroupFormModal`, which owns the API calls — this modal only
 * decides which group is being edited.
 */
const ManageUserRolesModal: React.FC<ManageUserRolesModalProps> = ({
  isOpen,
  onClose,
  groups,
  loading,
  onRefresh,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  // null while the form is open means "creating a new group".
  const [editingGroup, setEditingGroup] = useState<OgUserGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<OgUserGroup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { showToast } = useToastStore();

  const openForm = (group: OgUserGroup | null) => {
    setEditingGroup(group);
    setIsFormOpen(true);
  };

  const handleSaved = () => {
    setIsFormOpen(false);
    setEditingGroup(null);
    onRefresh();
  };

  /**
   * Only an empty group can be deleted. A group with assigned users returns an error
   * which is displayed as a single clear validation toast message.
   */
  const confirmDelete = async () => {
    if (!groupToDelete) return;
    setDeleting(true);
    try {
      await deleteOgUserGroup(groupToDelete.id);
      showToast(`${groupToDelete.roleName} deleted successfully.`, "success");
      setGroupToDelete(null);
      onRefresh();
    } catch (error) {
      showToast(
        getApiErrorMessage(error, "Cannot delete user role with assigned users."),
        "error"
      );
      setGroupToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // Esc and backdrop clicks reach every open modal at once — whatever is
  // stacked on top of this one has to be the one that closes first.
  const handleBackgroundClose = () => {
    if (!isFormOpen && !groupToDelete) onClose();
  };

  return (
    <>
      <AdminModal
        isOpen={isOpen}
        onClose={handleBackgroundClose}
        title="Manage Users Role"
        size="2xl"
        showCloseButton={false}
        headerAction={
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#E0A82E] rounded-lg text-sm font-medium text-white hover:opacity-90"
            onClick={() => openForm(null)}
          >
            <span className="st-add text-white" aria-hidden="true" />
            Add New User Role
          </button>
        }
        footer={
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        }
      >
        <div className="relative overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-sm text-lightgray-200 bg-gray-50 whitespace-nowrap">
              <tr>
                <th scope="col" className="px-6 py-4 font-normal border-r border-gray-200">
                  User Role
                </th>
                <th scope="col" className="px-6 py-4 font-normal border-r border-gray-200">
                  User Count
                </th>
                <th scope="col" className="px-6 py-4 font-normal border-r border-gray-200">
                  Free Tokens
                </th>
                <th scope="col" className="px-6 py-4 font-normal text-center border-r border-gray-200">
                  Edit
                </th>
                <th scope="col" className="px-6 py-4 font-normal text-center">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-lightgray-200 text-sm">
                    No user roles configured yet.
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <tr key={group.id} className="border-t border-gray-200">
                    <td className="px-6 py-4 border-r border-gray-200 max-w-[280px]">
                      <p className="font-semibold text-grease-100 whitespace-normal break-words">
                        {group.roleName}
                      </p>
                      {group.description && (
                        <p className="text-xs text-lightgray-200 mt-1 whitespace-normal break-words">
                          {group.description}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-grease-100 border-r border-gray-200 whitespace-nowrap">
                      {group.userCount}
                    </td>
                    <td className="px-6 py-4 text-grease-100 border-r border-gray-200 whitespace-nowrap">
                      {formatFreeTokenAmount(group.freeTokenAmount)}
                    </td>
                    <td className="px-6 py-4 border-r border-gray-200">
                      <div className="flex items-center justify-center">
                        <button
                          type="button"
                          aria-label={`Edit ${group.roleName}`}
                          className="text-lightgray-200 hover:text-royalblue-100 transition-colors duration-150"
                          onClick={() => openForm(group)}
                        >
                          <svg
                            className="w-4 h-4"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={1.8}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center">
                        <button
                          type="button"
                          aria-label={`Delete ${group.roleName}`}
                          className="text-lightgray-200 hover:text-red-500 transition-colors duration-150"
                          onClick={() => {
                            setGroupToDelete(group);
                          }}
                        >
                          <svg
                            className="w-4 h-4"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={1.8}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </AdminModal>

      <OgUserGroupFormModal
        isOpen={isFormOpen}
        group={editingGroup}
        onClose={() => {
          setIsFormOpen(false);
          setEditingGroup(null);
        }}
        onSaved={handleSaved}
      />

      <AdminModal
        isOpen={!!groupToDelete}
        onClose={() => {
          if (!deleting) {
            setGroupToDelete(null);
          }
        }}
        title="Delete User Role"
        size="sm"
        showCloseButton={false}
      >
        <div className="flex flex-col items-center justify-center p-2 text-center">
          <p className="mb-6 text-sm text-gray-700">
            Are you sure you want to delete{" "}
            <span className="font-semibold text-grease-100">
              "{groupToDelete?.roleName}"
            </span>
            ?
          </p>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => setGroupToDelete(null)}
              disabled={deleting}
              className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleting}
              className="px-6 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {deleting ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Deleting...</span>
                </>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        </div>
      </AdminModal>
    </>
  );
};

export default ManageUserRolesModal;
